import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SectionDecor } from "@/components/Decor";
import {
  Sparkles,
  ArrowRight,
  Cpu,
  ShieldCheck,
  Lightbulb,
  Puzzle,
  Quote,
  Star,
} from "lucide-react";
import { api } from "@/lib/api";
import { StatCounter } from "@/components/StatCounter";
import { WorkshopCard } from "@/components/WorkshopCard";

const HERO = "https://static.prod-images.emergentagent.com/jobs/1304f033-c917-4974-91b0-9d4153e7c191/images/aa0043ab67526cccace4181be245d6397a1f06dfc18ff2e84b02492f32128e87.jpeg";

export default function Home() {
  const [stats, setStats] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/workshops").then((r) => setWorkshops(r.data.slice(0, 4))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 to-white">
        <div className="absolute -top-16 -left-16 w-72 h-72 bg-sky-200/50 animate-blob" />
        <div className="absolute top-40 -right-10 w-64 h-64 bg-amber-200/50 animate-blob" style={{ animationDelay: "2s" }} />
        <SectionDecor />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-700 text-sm font-bold"
            >
              <Sparkles className="w-4 h-4" /> Culture numérique · 3e à 6e primaire
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900 mt-6"
            >
              Apprendre l'informatique{" "}
              <span className="text-sky-500">en s'amusant</span>, à l'école.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed"
            >
              Nos animateurs viennent dans vos classes animer des ateliers ludiques
              alignés sur le référentiel FMTTN. Une pédagogie qui fonctionne même
              hors ligne.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link
                to="/nos-activites"
                data-testid="hero-cta-activities"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 hover:scale-105 transition-[transform,background-color]"
              >
                Découvrir les ateliers <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/enseignants"
                data-testid="hero-cta-teachers"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white border border-slate-200 text-slate-900 font-bold hover:border-sky-400 hover:text-sky-600 transition-colors"
              >
                Espace enseignants
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl shadow-sky-500/20 rotate-2">
              <img src={HERO} alt="Enfants et animateur" className="w-full h-[440px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-3xl shadow-xl p-4 flex items-center gap-3 animate-float">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600">
                <ShieldCheck className="w-6 h-6" />
              </span>
              <div>
                <p className="font-heading font-extrabold text-slate-900 leading-tight">Plan B</p>
                <p className="text-xs text-slate-500">100% hors ligne possible</p>
              </div>
            </div>
            <div className="absolute -top-5 -right-2 bg-amber-400 rounded-3xl shadow-xl px-5 py-4 animate-float-slow">
              <p className="font-heading font-black text-slate-900 text-2xl leading-none">8</p>
              <p className="text-xs font-bold text-slate-800">ateliers</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 bg-white rounded-3xl border border-slate-100 shadow-sm p-10">
          {stats.map((s) => (
            <StatCounter key={s.label} value={s.value} suffix={s.suffix} label={s.label} />
          ))}
        </div>
      </section>

      {/* MISSION / PILLARS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Notre mission</span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-900 mt-2">
            Former les citoyens numériques de demain
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Kwarizmix rend la culture numérique accessible, concrète et joyeuse.
            Chaque atelier mélange manipulation, jeu et réflexion critique.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {[
            { icon: Cpu, title: "Pensée algorithmique", desc: "Séquences, boucles et logique par le jeu.", c: "bg-sky-100 text-sky-600" },
            { icon: ShieldCheck, title: "Sécurité & citoyenneté", desc: "Mots de passe, vie privée, bienveillance.", c: "bg-emerald-100 text-emerald-600" },
            { icon: Lightbulb, title: "Création de contenus", desc: "Textes, images et publications responsables.", c: "bg-amber-100 text-amber-600" },
            { icon: Puzzle, title: "Esprit critique", desc: "Vérifier ses sources, déjouer les intox.", c: "bg-rose-100 text-rose-600" },
          ].map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]"
            >
              <span className={`grid place-items-center w-14 h-14 rounded-2xl mb-4 ${p.c}`}>
                <p.icon className="w-7 h-7" />
              </span>
              <h3 className="font-heading font-extrabold text-lg text-slate-900">{p.title}</h3>
              <p className="text-sm text-slate-500 mt-2">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WORKSHOPS PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Nos activités</span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-900 mt-2">
              Des ateliers pour chaque niveau
            </h2>
          </div>
          <Link
            to="/nos-activites"
            data-testid="home-view-all-workshops"
            className="inline-flex items-center gap-2 text-sky-600 font-bold hover:gap-3 transition-all"
          >
            Voir tous les ateliers <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {workshops.map((w, i) => (
            <WorkshopCard key={w.id} workshop={w} index={i} />
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">Témoignages</span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-white mt-2">
              Ils nous font confiance
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-slate-800 rounded-3xl p-6 border border-slate-700"
              >
                <Quote className="w-8 h-8 text-sky-400 mb-4" />
                <p className="text-slate-200 text-sm leading-relaxed mb-5">"{t.text}"</p>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="font-bold text-white">{t.name}</p>
                <p className="text-xs text-slate-400">{t.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-sky-500 px-8 py-16 sm:px-16 text-center">
          <div className="absolute -top-10 -right-10 w-48 h-48 bg-sky-400 rounded-full opacity-50" />
          <div className="absolute -bottom-12 -left-8 w-56 h-56 bg-sky-600 rounded-full opacity-40" />
          <div className="relative">
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-white">
              Envie d'un atelier dans votre classe ?
            </h2>
            <p className="mt-4 text-sky-50 max-w-xl mx-auto text-lg">
              Demandez un devis personnalisé ou réservez directement l'un de nos ateliers.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/nos-activites" className="px-7 py-3.5 rounded-full bg-white text-sky-600 font-bold hover:scale-105 transition-transform">
                Réserver un atelier
              </Link>
              <Link to="/contact" className="px-7 py-3.5 rounded-full bg-slate-900 text-white font-bold hover:scale-105 transition-transform">
                Demander un devis
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
